// components
import { Layout, TextInput, ImageArea, SelectBox, PrimaryButton } from '../components'
// npm
import { useEffect, useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { push } from 'connected-react-router'
import styled from 'styled-components'
import { Editor } from '@tinymce/tinymce-react'
// firebase
import { db, FirebaseTimestamp } from "../firebase"
// Mui
import TextField from '@material-ui/core/TextField'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import AddBoxIcon from '@material-ui/icons/AddBox'
import DeleteIcon from '@material-ui/icons/Delete'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import IconButton from '@material-ui/core/IconButton'
import Switch from '@material-ui/core/Switch'

const magazinesRef = db.collection('magazines')

// MCE設定
const options = {
    language: 'ja',
    height: 200,
    plugins: 'lists link paste',
    menubar: false,
    statusbar: false,
    paste_as_text: true,
    toolbar: "bold italic | bullist numlist | link",
}

const MagazineEdit = () => {
    const dispatch = useDispatch()
    
    // URLからID取得
    let magazineId = window.location.pathname.split('/magazine/edit')[1];
    if (magazineId !== '') { magazineId = magazineId?.split('/')[1] }

    // 入力内容初期化
    const [image, setImage] = useState([])
    const [imagePosition, setImagePosition] = useState('')
    const [title, setTitle] = useState('')
    const [subTitle, setSubTitle] = useState('')
    const [brand, setBrand] = useState('')
    const [brands, setBrands] = useState([])
    const [category, setCategory] = useState('')
    const [categories, setCategories] = useState([])
    const [external, setExternal] = useState(false)
    const [externalLink, setExternalLink] = useState('')
    const [products, setProducts] = useState([])
    const [inputList, setInputList] = useState([{ 
        sectionType: "", order: 999,
        text: "", 
        image: [], caption: "", imagePosition: "", 
        link: "" 
    }])
    const [metaTitle, setMetaTitle] = useState('')
    const [metaDescription, setMetaDescription] = useState('')
    const [metaKeywords, setMetaKeywords] = useState([])
    const [isDraft, setIsDraft] = useState(false)
    const [isTrash, setIsTrash] = useState(false)

    // ゴミ箱移動処理
    const moveToTrash = useCallback(()=>{
        return magazinesRef.doc(magazineId).set({isTrash: true}, {merge: true})
                      .then(() => {dispatch(push('/admin/magazine'))})
                      .catch( error => {throw new Error(error)})
    },[magazineId, dispatch])
    const moveFromTrash = useCallback(()=>{
        return magazinesRef.doc(magazineId).set({isTrash: false}, {merge: true})
                      .then(() => {dispatch(push('/admin/magazine'))})
                      .catch( error => {throw new Error(error)})
    },[magazineId, dispatch])

    const handleImageAreaChange = ( index, newImage ) => {
        const list = [...inputList]
        list[index].image = newImage
        setInputList(list);
    }
    const handleInputChange = ( e, index ) => {
        const { name, value } = e.target
        const list = [...inputList]
        list[index][name] = value
        setInputList(list);
    }
    // リッチエディタ（文字数／HTML取得）
    const handleRichEditor = useCallback( (e, index) => {
        const value = e.target.getContent()
        console.log(value)
        const list = [...inputList]
        list[index]['text'] = value
        setInputList(list);
    },[inputList])

    const handleSelectBoxChange = ( selectedProduct, index ) => {
        const list = [...inputList]
        list[index].link = selectedProduct
        setInputList(list);
    }

    const handleSectionUp = index => {
        const list = [...inputList]
        list[index].order = list[index].order - 1
        list[index-1].order = list[index].order + 1
        list.sort((a,b) => {
            if(a.order < b.order) return -1
            if(a.order > b.order) return 1
            return 0
        })
        setInputList(list);
    }
    const handleSectionDown = index => {
        const list = [...inputList]
        list[index].order = list[index].order + 1
        list[index+1].order = list[index].order - 1
        list.sort((a,b) => {
            if(a.order < b.order) return -1
            if(a.order > b.order) return 1
            return 0
        })
        setInputList(list);
    }

    const handleAddClick = index => {
        setInputList([...inputList, { 
            sectionType: "", order: index+2, text: "", image: [], 
            caption: "", imagePosition: "", link: "" 
        }]);
    }

    const handleRemoveClick = index => {
        // 確認
        const list = [...inputList]
        const conf = window.confirm('一度削除すると元に戻せません。削除してよろしいですか？')
        if (conf) {
            
            // 既存のものであればFirestoreから削除
            if (list[index].id) {
                magazinesRef.doc(magazineId)
                  .collection('sections').doc(list[index].id)
                  .delete()
            }
            // Stateから削除
            list.splice(index, 1);
            setInputList(list);
        } else {
            return;
        }
    }

    // 保存処理
    const saveMagazine = (magazineId, image, imagePosition, title, subTitle, category, brand, external, externalLink, inputList, metaTitle, metaDescription, metaKeywords, isDraft, isTrash ) => {
        return async (dispatch) => {

            // メイン画像 Validation
            if (!image || image.length === 0) {
                alert('メイン画像が設定されていません')
                return false
            }
            // タイトル Validation
            if (title === '') {
                alert('タイトルが設定されていません')
                return false
            }

            // Validation
            if (external) {
                const regexURL = /^https?:/
                if (externalLink === '' ) {
                    alert('外部リンクURLが未入力です')
                    return false
                } else if (!regexURL.test(externalLink)) {
                    alert('外部リンクURLが不正です。http(s)から始まるURLを入力してください')
                    return false
                }
            } else {
                if (imagePosition === '' || subTitle === '' || category === undefined ) {
                    alert('未入力の必須項目があります')
                    return false
                }
            }

            const timestamp = FirebaseTimestamp.now()
    
            // 基本データセット
            const data = {
                image, imagePosition, title, subTitle, category, 
                brand: brand ? brand : "", 
                external, externalLink, 
                metaTitle: metaTitle ? metaTitle : "",
                metaDescription: metaDescription ? metaDescription : "", 
                metaKeywords: metaKeywords ? metaKeywords : "",
                isDraft, isTrash,
                updated_at: timestamp
            }
            // 新規作成の場合はpostIdと作成日のデータを追加でセット
            if (magazineId === '') {
                magazineId = magazinesRef.doc().id
                data.id = magazineId
                data.created_at = timestamp
            }
    
            const sectionsRef = magazinesRef.doc(magazineId).collection('sections')
    
            // セクション毎のデータセット
            const sectionsData = []
            for (const [key, section] of inputList.entries()) {
                // Validation
                if (section.sectionType === '') {
                    alert(`セクション${key+1}のセクションタイプが選択されていません`)
                    return false
                } else if (section.sectionType === 'text') {
                    if (section.text === '') {
                        alert(`セクション${key+1}のテキストが入力されていません`)
                        return false
                    }
                } else if (section.sectionType === 'image') {
                    if (!section.image || section.image.length === 0) {
                        alert(`セクション${key+1}の画像が設定されていません`)
                        return false
                    }
                    if (section.imagePosition === '') {
                        alert(`セクション${key+1}の画像位置が選択されていません`)
                        return false
                    } else if (section.imagePosition === 'left' || section.imagePosition === 'right' ) {
                        if (section.caption === '') {
                            alert(`セクション${key+1}のキャプションが入力されていません`)
                            return false
                        }
                    }
                } else if (section.sectionType === 'link') {
                    if (section.caption === '') {
                        alert(`セクション${key+1}の商品リンクが選択されていません`)
                        return false
                    }
                }
                sectionsData[key] = {
                    sectionType: section.sectionType,
                    order: key+1,
                    text: section.text, 
                    image: section.image,
                    caption: section.caption,
                    imagePosition: section.imagePosition,
                    link: section.link,
                    updated_at: timestamp
                }
                if (!section.id) {
                    const newSectionId = sectionsRef.doc().id
                    sectionsData[key].id = newSectionId
                    sectionsData[key].created_at = timestamp
                } else {
                    sectionsData[key].id = section.id
                }
                
            }
    
            // Firestoreに保存
            magazinesRef.doc(magazineId)
                           .set(data, {merge: true})
                           .then( () => {
                                // Sectionsの内容を保存
                                inputList.forEach((x,key)=>{
                                    sectionsRef.doc(sectionsData[key].id)
                                               .set(sectionsData[key], {merge: true})
                                })
                                dispatch(push('/admin/magazine')) 
                            })
                           .catch( error => {
                                throw new Error(error)
                           })
        }
    }

    // Brandリスト取得
    useEffect( () => {
        db.collection('brands')
          .orderBy('order','asc')
          .get()
          .then( snapshots => {
              const list = []
              snapshots.forEach(snapshot => {
                  const data = snapshot.data()
                  list.push({
                      id: data.id,
                      name: data.name
                  })
              })
              setBrands(list)
          })
        return false
    }, []);
    // Categoriesリスト取得
    useEffect( () => {
        db.collection('categories')
          .get()
          .then( snapshots => {
              const list = []
              snapshots.forEach(snapshot => {
                  const data = snapshot.data()
                  list.push({
                      id: data.id,
                      name: data.name
                  })
              })
              setCategories(list)
          })
        return false
    }, [])
    // Productリスト取得
    useEffect( () => {
        db.collection('products')
          .get()
          .then( snapshots => {
              const list = []
              snapshots.forEach(snapshot => {
                  const data = snapshot.data()
                  list.push({
                      id: data.id,
                      name: data.name
                  })
              })
              setProducts(list)
          })
        return false
    }, []);

    useEffect( () => {
        if (magazineId) {
            magazinesRef.doc(magazineId).get()
                        .then(snapshot => {
                            const data = snapshot.data()
                            setImage(data.image)
                            setImagePosition(data.imagePosition)
                            setTitle(data.title)
                            setSubTitle(data.subTitle)
                            setCategory(data.category)
                            setBrand(data.brand)
                            setExternal(data.external)
                            setExternalLink(data.externalLink)
                            setMetaTitle(data.metaTitle)
                            setMetaDescription(data.metaDescription)
                            setMetaKeywords(data.metaKeywords)
                            setIsDraft(data.isDraft)
                            setIsTrash(data.isTrash)
                        })
                        .then(()=>{
                            magazinesRef.doc(magazineId).collection('sections').orderBy('order','asc').get()
                                        .then(snapshots=>{
                                            const sectionTempList = []
                                            snapshots.forEach(snapshot => {
                                                const section = snapshot.data()
                                                sectionTempList.push(section)
                                            })
                                            setInputList(sectionTempList)
                                        })
                        })
        }
        return false
    }, [magazineId]);

    return (
        <Layout>
            <div className='spacer-100' />
            <h2>マガジンの投稿・編集</h2>
            <div className='spacer-50' />

            <FormWrapper>
                <SwitchWrapper>
                    <div>外部リンク</div>
                    <span style={{color:'#777',fontSize:'.8rem'}}>外部リンクに飛ばす場合のみチェック</span>
                    <Switch
                        color="primary" checked={external}
                        onChange={()=>setExternal(!external)}
                    />
                </SwitchWrapper>
                <div className='spacer-50'></div>
                <ImageArea image={image} setImage={setImage} />
                <div className='spacer-50'></div>
                {!external && (
                    <FormControl component="fieldset" required={true}>
                        <FormLabel component="legend">画像位置</FormLabel>
                        <RadioWrapper value={imagePosition} onChange={e=>setImagePosition(e.target.value)} >
                            <FormControlLabel value="left" control={<Radio color="primary" />} label="左寄せ" />
                            <FormControlLabel value="right" control={<Radio color="primary" />} label="右寄せ" />
                            <FormControlLabel value="hidden" control={<Radio color="primary" />} label="非表示" />
                        </RadioWrapper>
                        <div className='spacer-25'></div>
                    </FormControl>
                )}
                <TextInput 
                    fullWidth={true} label={'タイトル'} multiline={false} rows={1} required={true} type={'text'} 
                    value={title} onChange={e=>setTitle(e.target.value)}
                />
                {external && (
                    <TextInput 
                        fullWidth={true} label={'外部リンク'} multiline={false} rows={1} required={true} type={'url'} 
                        value={externalLink} onChange={e=>setExternalLink(e.target.value)}
                    />
                )}
                {!external && (
                    <>
                        <TextInput 
                            fullWidth={true} label={'サブタイトル'} multiline={false} rows={1} required={true} type={'text'} 
                            value={subTitle} onChange={e=>setSubTitle(e.target.value)}
                        />
                        <SelectBox 
                            label={'カテゴリ'}　required={true} select={setCategory} value={category} options={categories} 
                        />
                        <SelectBox 
                            label={'ブランド'}　required={false} select={setBrand} value={brand} options={brands} 
                        />
                        <div className='spacer-50'></div>
                    

                    {/* ------------------ セクション ------------------- */}
                    {inputList.map((x, i) => {
                        return (
                            <SectionWrapper key={i}>
                                <div className='spacer-25'/>
                                <strong>セクション{i+1}</strong>
                                <div className='spacer-25'/>
                                <FormControl component="fieldset" required={true}>
                                    <FormLabel component="legend">セクションタイプ</FormLabel>
                                    <RadioWrapper name="sectionType" value={x.sectionType} onChange={e => handleInputChange(e, i)} >
                                        <FormControlLabel value="text" control={<Radio color="primary" />} label="テキスト" />
                                        <FormControlLabel value="image" control={<Radio color="primary" />} label="画像とキャプション" />
                                        <FormControlLabel value="link" control={<Radio color="primary" />} label="商品リンク" />
                                    </RadioWrapper>
                                </FormControl>
                                <div className='spacer-25'/>
                                { x.sectionType === "text" && ( 
                                    <Editor 
                                        init={options} initialValue={x.text} name="text" 
                                        onChange={e => handleRichEditor(e,i)} 
                                    />
                                )}
                                { x.sectionType === "image" && ( 
                                    <>
                                        <ImageArea image={x.image} setImage={newImage=>handleImageAreaChange(i, newImage)} />
                                        <div className='spacer-25' />
                                        <FormControl component="fieldset" required={true}>
                                            <FormLabel component="legend">画像位置</FormLabel>
                                            <RadioWrapper name="imagePosition" value={x.imagePosition} onChange={e=>handleInputChange(e, i)} >
                                                <FormControlLabel value="left" control={<Radio color="primary" />} label="左寄せ" />
                                                <FormControlLabel value="center" control={<Radio color="primary" />} label="中央（キャプションなし）" />
                                                <FormControlLabel value="right" control={<Radio color="primary" />} label="右寄せ" />
                                            </RadioWrapper>
                                        </FormControl>
                                        <div className='spacer-25' />
                                        <TextField
                                            label={'キャプション'} fullWidth={true} multiline={true} rows={3} required={x.imagePosition === 'left' || x.imagePosition === 'right'}
                                            name="caption" 
                                            value={x.caption} onChange={e => handleInputChange(e, i)}
                                        />
                                    </>
                                )}
                                { x.sectionType === "link" && ( 
                                    <SelectBox 
                                        label={'商品'}　required={true} select={e=>handleSelectBoxChange(e, i)} options={products} 
                                        name="link" value={x.link}
                                    />
                                )}
                                
                                <div>
                                    {inputList.length !== 1 && (
                                        <>
                                            <DeleteButton onClick={()=>handleRemoveClick(i)}>
                                                <DeleteIcon/>
                                            </DeleteButton>
                                            { i !== 0 && (
                                                <UpButton onClick={()=>handleSectionUp(i)}>
                                                    <ArrowUpwardIcon/>
                                                </UpButton>
                                            )}
                                            { i !== inputList.length - 1 && (
                                                <DownButton onClick={()=>handleSectionDown(i)}>
                                                    <ArrowDownwardIcon />
                                                </DownButton>
                                            )}
                                        </>
                                    )}
                                    {inputList.length - 1 === i && (
                                        <AddButton onClick={()=>handleAddClick(i)}>
                                            <AddBoxIcon color={"primary"} fontSize={'large'} />
                                        </AddButton>
                                    )}
                                    <div className='spacer-50'></div>
                                </div>
                            </SectionWrapper>
                        );
                    })}
                    <div className='spacer-50' />
                    <TextInput 
                        fullWidth={true} label={'titleメタデータ'} multiline={false} rows={1} type={'text'} 
                        value={metaTitle} onChange={e=>setMetaTitle(e.target.value)}
                    />
                    <TextInput 
                        fullWidth={true} label={'descriptionメタデータ'} multiline={true} rows={2} type={'text'} 
                        value={metaDescription} onChange={e=>setMetaDescription(e.target.value)}
                    />
                    <TextInput 
                        fullWidth={true} label={'keywordsメタデータ'} multiline={false} rows={1} type={'text'} 
                        value={metaKeywords} onChange={e=>setMetaKeywords(e.target.value)}
                    />
                    <div className='spacer-50' />
                    </>
                )}
                <ButtonWrapper>
                    <div className='spacer-100'></div>
                    <SwitchWrapper>
                        <div>下書きとして保存</div>
                        <Switch
                            color="primary" checked={isDraft}
                            onChange={()=>setIsDraft(!isDraft)}
                        />
                    </SwitchWrapper>
                    <div className='spacer-25' />
                    <PrimaryButton 
                        label={isDraft ? '下書きを保存する' : '公開する'} 
                        operation={ () => dispatch( saveMagazine( 
                            magazineId, image, imagePosition, 
                            title, subTitle, category, brand, external, externalLink, inputList,
                            metaTitle, metaDescription, metaKeywords, isDraft, isTrash
                        ))} 
                    />
                    <div className='spacer-25' />
                    {isTrash ? (
                        <button onClick={moveFromTrash}>ゴミ箱から戻す</button>
                    ) : (
                        <button onClick={moveToTrash}>ゴミ箱に入れる</button>
                    )}
                    <div className='spacer-25' />
                    <button onClick={()=> dispatch(push('/admin/magazine'))}>キャンセル</button>
                </ButtonWrapper>
                {/* デバック用 */}
                {/* <div style={{ marginTop: 20 }}>{JSON.stringify(inputList)}</div> */}
            </FormWrapper>
            
            <div className='spacer-100' />
        </Layout>
    )
}

export default MagazineEdit

const FormWrapper = styled.div`
    width: 480px;
    margin: 0 auto;
`
const ButtonWrapper = styled(FormWrapper)`
    text-align: center;
`
const RadioWrapper = styled(RadioGroup)`
    display: flex !important;
    flex-direction: row !important;
    justify-content: center;
    align-items: center;
`
const SectionWrapper = styled.div`
    border: 1px solid #ccc;
    padding: 20px;
    margin-bottom: 30px;
    position: relative !important;
`
const DeleteButton = styled(IconButton)`
    position: absolute !important;
    bottom: 10px;
    right: 10px;
`
const AddButton = styled(IconButton)`
    position: absolute !important;
    left: 50%;
    transform: translateX(-50%);
    bottom: -75px;
`
const UpButton = styled(IconButton)`
    position: absolute !important;
    top: 10px !important;
    left: 50% !important;
    transform: translateX(-50%);
`
const DownButton = styled(IconButton)`
    position: absolute !important;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
`
const SwitchWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`